/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePostHog } from "posthog-js/react";

const FILTER_URL_SYNC_ENV_KEY = "NEXT_PUBLIC_ENABLE_FILTER_URL_SYNC";

const BOOLEAN_TRUE_VALUES = new Set(["1", "true", "yes", "on"]);

const parseBooleanEnv = (value: string | undefined): boolean => {
  if (!value) {
    return false;
  }
  return BOOLEAN_TRUE_VALUES.has(value.trim().toLowerCase());
};

export type FeatureFlags = Record<string, boolean>;

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export interface FeatureFlagsProviderProps {
  children: React.ReactNode;
  initialFlags?: FeatureFlags;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
  initialFlags,
}) => {
  const posthog = usePostHog();
  const [phFlags, setPhFlags] = useState<FeatureFlags>({});

  useEffect(() => {
    if (!posthog) return;
    posthog.onFeatureFlags((flags) => {
      const resolved: FeatureFlags = {};
      flags.forEach((flag) => {
        resolved[flag] = posthog.isFeatureEnabled(flag) ?? false;
      });
      setPhFlags(resolved);
    });
  }, [posthog]);

  const envMaintenance = (
    process.env["NEXT_PUBLIC_MAINTENANCE_MODE"] ?? ""
  ).toLowerCase();
  const maintenanceEnabled =
    envMaintenance === "1" || envMaintenance === "true";
  const filterUrlSyncEnvEnabled = parseBooleanEnv(
    process.env[FILTER_URL_SYNC_ENV_KEY],
  );
  const filterUrlSyncOrgEnabled = Boolean(
    initialFlags?.["filter_url_sync"] ?? false,
  );
  const filterUrlSyncEnabled =
    filterUrlSyncEnvEnabled && filterUrlSyncOrgEnabled;

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      flags: {
        maintenance_mode: maintenanceEnabled,
        ...(initialFlags ?? {}),
        filter_url_sync: filterUrlSyncEnabled,
        // PostHog flags override env-based flags (PostHog is primary)
        ...phFlags,
      },
    }),
    [maintenanceEnabled, initialFlags, filterUrlSyncEnabled, phFlags],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlag(flag: string): boolean {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) return false;
  return !!ctx.flags[flag];
}

export function useFeatureFlags(): FeatureFlags {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) return {};
  return ctx.flags;
}
